import React from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useColorPalette } from '../../contexts/ColorPaletteContext';
import { formatDateShort } from '../../utils/todayUtils';
import WeatherIcon from '../ui/WeatherIcon';
import PropTypes from 'prop-types';

const WeeklyCell = ({ day, isCurrentYear = true, getTodaysDate, isCompactMode = false, isUltraCompact = false, events = [], isEnglish = false }) => {
    const { getCellColor, getTextColor } = useColorPalette();
    const isMobile = useMediaQuery('(max-width:768px)');
    const isSmallMobile = useMediaQuery('(max-width:480px)');
    
    // 空のセルの場合の処理
    if (day.is_empty) {
        return (
            <Box 
                sx={{ 
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#f9f9f9',
                    border: '1px solid #eee',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
            </Box>
        );
    }

    
    const todaysDate = getTodaysDate();
    const isToday = day.date === todaysDate || day.is_today;
    const hasData = day.congestion_level > 0;
    const isFuture = day.is_future || false;
    const isPrediction = day.source === 'prediction';
    const showEvents = Boolean(events && events.length > 0);

    // 今日からの日数を計算
    const today = new Date(todaysDate);
    const currentDay = new Date(day.date);
    const daysDiff = Math.round((currentDay - today) / (1000 * 60 * 60 * 24));
    
    // 数値可読性を優先するため、通常セルでは相対日付ラベルを表示しない
    const dateLabel = '';

    const cellStyle = {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isUltraCompact ? '0px' : (isCompactMode ? '1px' : (isSmallMobile ? '1px' : '2px')),
        position: 'relative',
        flexShrink: 0,
        padding: isUltraCompact ? (isMobile ? '4px 0px' : '0px') : (isMobile ? '5px 1px' : '2px'),
        border: isToday ? '2px solid #1976d2' : 'none',
        boxSizing: 'border-box',
    };

    // 未来の日付かつ予測データがない場合のフォールバック表示
    if (isFuture && isCurrentYear && !hasData && daysDiff >= 1 && daysDiff <= 28) {
        let displayText = '';
        if (daysDiff === 1) displayText = isEnglish ? 'In 1 day' : '1日後';
        else if (daysDiff === 2) displayText = isEnglish ? 'In 2 days' : '2日後';
        else displayText = isEnglish ? `In ${daysDiff} days` : `${daysDiff}日後`;
        
        const labelColor = daysDiff <= 2 ? '#4caf50' : '#9e9e9e';
        const hasEvents = showEvents;
        
        return (
            <Box 
                sx={{ 
                    ...cellStyle,
                    backgroundColor: '#f9f9f9',
                    color: '#333',
                    border: `1px solid #ddd`,
                    borderTop: `1px solid #ddd`,
                    borderBottom: `1px solid #ddd`
                }}
            >
                {/* メインコンテンツエリア */}
                <Box sx={{ 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0px'
                }}>
                    {/* 日付と天気を同じ行に表示 */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '2px',
                        width: '100%'
                    }}>
                        <Typography 
                            sx={{
                                fontSize: isUltraCompact 
                                    ? (isSmallMobile ? '12px' : '14px')
                                    : (isCompactMode 
                                        ? (isSmallMobile ? '14px' : '16px') 
                                        : (isSmallMobile ? '16px' : isMobile ? '18px' : '20px')),
                                lineHeight: '1',
                                fontWeight: '500',
                                textAlign: 'center',
                                mb: 0,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                            }}
                        >
                            {formatDateShort(day.date)}
                        </Typography>
                        
                        {/* 天気アイコンを日付の横に表示 */}
                        {day.weather_info && (
                            <Box sx={{
                                width: isUltraCompact ? '12px' : (isSmallMobile ? '14px' : isMobile ? '16px' : '18px'),
                                height: isUltraCompact ? '12px' : (isSmallMobile ? '14px' : isMobile ? '16px' : '18px'),
                                backgroundColor: 'rgba(255, 255, 255, 0.85)',
                                borderRadius: '2px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                            }}>
                                <WeatherIcon 
                                    weather={day.weather_info.weather}
                                    size="small"
                                    showTemp={false}
                                />
                            </Box>
                        )}
                    </Box>
                    
                    <Typography 
                        sx={{ 
                            fontSize: isUltraCompact
                                ? (isSmallMobile ? '10px' : '11px')
                                : (isCompactMode
                                    ? (isSmallMobile ? '12px' : '13px')
                                    : (isSmallMobile ? '14px' : isMobile ? '16px' : '18px')),
                            lineHeight: '1',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            color: labelColor
                        }}
                    >
                        {displayText}
                    </Typography>
                </Box>
                
                {/* イベント情報エリア */}
                {hasEvents && (
                    <Box sx={{
                        width: '100%',
                        minHeight: isUltraCompact ? '14px' : (isSmallMobile ? '16px' : '18px'),
                        backgroundColor: 'rgba(255, 255, 255, 0.75)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '1px 2px',
                        overflow: 'visible'
                    }}>
                        <Typography 
                            sx={{ 
                                fontSize: isUltraCompact ? '11px' : (isSmallMobile ? '13px' : '14px'),
                                color: '#333',
                                fontWeight: '600',
                                lineHeight: '1.1',
                                textAlign: 'center',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                hyphens: 'auto',
                                maxWidth: '100%'
                            }}
                            title={events.map(e => e.title).join(', ')}
                        >
                        {isToday
                            ? (isEnglish ? `${events.length} events` : `${events.length}件`)
                            : events.map((event, index) => (
                                <span key={index}>
                                    {event.title}
                                    {index < events.length - 1 && (
                                        <br />
                                    )}
                                </span>
                            ))}
                        </Typography>
                    </Box>
                )}

            </Box>
        );
    }

    // 今日のデータがない場合の特別表示
    if (isToday && !hasData) {
        const hasEvents = showEvents;
        
        return (
            <Box 
                sx={{ 
                    ...cellStyle,
                    backgroundColor: '#f5f5f5',
                    color: '#333',
                    border: '3px solid #1976d2',
                    borderTop: '3px solid #1976d2',
                    borderBottom: '3px solid #1976d2'
                }}
            >
                {/* メインコンテンツエリア */}
                <Box sx={{ 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0px'
                }}>
                    {/* 日付と天気を同じ行に表示 */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '2px',
                        width: '100%'
                    }}>
                        <Typography 
                            sx={{
                                fontSize: isUltraCompact 
                                    ? (isSmallMobile ? '12px' : '14px')
                                    : (isCompactMode 
                                        ? (isSmallMobile ? '14px' : '16px') 
                                        : (isSmallMobile ? '16px' : isMobile ? '18px' : '20px')),
                                lineHeight: '1',
                                fontWeight: '500',
                                textAlign: 'center',
                                mb: 0,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                            }}
                        >
                            {formatDateShort(day.date)}
                        </Typography>
                        
                        {/* 天気アイコンを日付の横に表示 */}
                        {day.weather_info && (
                            <Box sx={{
                                width: isUltraCompact ? '12px' : (isSmallMobile ? '14px' : isMobile ? '16px' : '18px'),
                                height: isUltraCompact ? '12px' : (isSmallMobile ? '14px' : isMobile ? '16px' : '18px'),
                                backgroundColor: 'rgba(255, 255, 255, 0.85)',
                                borderRadius: '2px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                            }}>
                                <WeatherIcon 
                                    weather={day.weather_info.weather}
                                    size="small"
                                    showTemp={false}
                                />
                            </Box>
                        )}
                    </Box>
                    
                    <Typography 
                        sx={{ 
                            fontSize: isUltraCompact
                                ? (isSmallMobile ? '10px' : '12px')
                                : (isCompactMode
                                    ? (isSmallMobile ? '12px' : '14px')
                                    : (isSmallMobile ? '14px' : isMobile ? '16px' : '18px')),
                            lineHeight: '1',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            color: '#1976d2'
                        }}
                    >
                                            {isEnglish ? 'Today' : '今日'}
                </Typography>
            </Box>
            
            {/* イベント情報エリア */}
            {hasEvents && (
                <Box sx={{
                    width: '100%',
                    minHeight: isUltraCompact ? '10px' : (isSmallMobile ? '12px' : '14px'),
                    backgroundColor: 'rgba(255, 255, 255, 0.75)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1px 2px',
                    overflow: 'visible'
                }}>
                    <Typography 
                        sx={{ 
                            fontSize: isUltraCompact ? '7px' : (isSmallMobile ? '8px' : '9px'),
                            color: '#333',
                            fontWeight: '600',
                            lineHeight: '1.1',
                            textAlign: 'center',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            hyphens: 'auto',
                            maxWidth: '100%'
                        }}
                        title={events.map(e => e.title).join(', ')}
                    >
                        {isToday
                            ? (isEnglish ? `${events.length} events` : `${events.length}件`)
                            : (events.length > 1 ? (isEnglish ? `${events.length} events` : `${events.length}件`) : events[0]?.title?.substring(0, 4))}
                    </Typography>
                </Box>
            )}
            </Box>
        );
    }

    // データがない場合のスタイル（未来の日付以外）
    if (!hasData && !isFuture) {
        const hasEvents = showEvents;
        
        return (
            <Box 
                sx={{ 
                    ...cellStyle,
                    backgroundColor: '#e0e0e0',
                    color: '#666'
                }}
            >
                {/* メインコンテンツエリア */}
                <Box sx={{ 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0px'
                }}>
                    {/* 日付と天気を同じ行に表示 */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '2px',
                        width: '100%'
                    }}>
                        <Typography 
                            sx={{
                                fontSize: isUltraCompact 
                                    ? (isSmallMobile ? '12px' : '14px')
                                    : (isCompactMode 
                                        ? (isSmallMobile ? '14px' : '16px') 
                                        : (isSmallMobile ? '16px' : isMobile ? '18px' : '20px')),
                                lineHeight: '1',
                                fontWeight: '500',
                                textAlign: 'center',
                                mb: 0,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                            }}
                        >
                            {formatDateShort(day.date)}
                        </Typography>
                        
                        {/* 天気アイコンを日付の横に表示 */}
                        {day.weather_info && (
                            <Box sx={{
                                width: isUltraCompact ? '12px' : (isSmallMobile ? '14px' : isMobile ? '16px' : '18px'),
                                height: isUltraCompact ? '12px' : (isSmallMobile ? '14px' : isMobile ? '16px' : '18px'),
                                backgroundColor: 'rgba(255, 255, 255, 0.85)',
                                borderRadius: '2px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                            }}>
                                <WeatherIcon 
                                    weather={day.weather_info.weather}
                                    size="small"
                                    showTemp={false}
                                />
                            </Box>
                        )}
                    </Box>
                    
                    <Typography 
                        sx={{ 
                            fontSize: isUltraCompact
                                ? (isSmallMobile ? '10px' : '12px')
                                : (isCompactMode
                                    ? (isSmallMobile ? '12px' : '14px')
                                    : (isSmallMobile ? '14px' : isMobile ? '16px' : '18px')),
                            lineHeight: '1',
                            fontWeight: 'bold',
                            textAlign: 'center'
                        }}
                    >
                                            {dateLabel || '-'}
                </Typography>
            </Box>
            
            {/* イベント情報エリア */}
            {hasEvents && (
                <Box sx={{
                    width: '100%',
                    minHeight: isUltraCompact ? '10px' : (isSmallMobile ? '12px' : '14px'),
                    backgroundColor: 'rgba(255, 255, 255, 0.75)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1px 2px',
                    overflow: 'visible'
                }}>
                    <Typography 
                        sx={{ 
                            fontSize: isUltraCompact ? '7px' : (isSmallMobile ? '8px' : '9px'),
                            color: '#333',
                            fontWeight: '600',
                            lineHeight: '1.1',
                            textAlign: 'center',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            hyphens: 'auto',
                            maxWidth: '100%'
                        }}
                        title={events.map(e => e.title).join(', ')}
                    >
                        {isToday
                            ? (isEnglish ? `${events.length} events` : `${events.length}件`)
                            : (events.length > 1 ? (isEnglish ? `${events.length} events` : `${events.length}件`) : events[0]?.title?.substring(0, 4))}
                    </Typography>
                </Box>
            )}
            </Box>
        );
    }
    
    // データがある場合の通常表示
    const cellColor = getCellColor(day.congestion_level);
    const textColor = getTextColor(day.congestion_level);
    const hasEvents = showEvents;
    
    return (
        <Box 
            sx={{ 
                ...cellStyle,
                backgroundColor: cellColor,
                color: textColor
            }}
        >
            {/* メインコンテンツエリア */}
            <Box sx={{ 
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isUltraCompact ? '0px' : '1px'
            }}>
                {/* 日付と天気を同じ行に表示 */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '2px',
                    width: '100%'
                }}>
                    <Typography 
                        sx={{
                            fontSize: isUltraCompact 
                                ? (isSmallMobile ? '10px' : '12px')
                                : (isCompactMode 
                                    ? (isSmallMobile ? '12px' : '14px') 
                                    : (isSmallMobile ? '14px' : isMobile ? '16px' : '18px')),
                            lineHeight: '1',
                            fontWeight: '500',
                            textAlign: 'center',
                            mb: 0,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                        }}
                    >
                        {formatDateShort(day.date)}
                    </Typography>
                    
                    {/* 天気アイコンを日付の横に表示 */}
                    {day.weather_info && (
                        <Box sx={{
                            width: isUltraCompact ? '12px' : (isSmallMobile ? '14px' : isMobile ? '16px' : '18px'),
                            height: isUltraCompact ? '12px' : (isSmallMobile ? '14px' : isMobile ? '16px' : '18px'),
                            backgroundColor: 'rgba(255, 255, 255, 0.85)',
                            borderRadius: '2px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                        }}>
                            <WeatherIcon 
                                weather={day.weather_info.weather}
                                size="small"
                                showTemp={false}
                            />
                        </Box>
                    )}
                </Box>
                
                <Typography 
                    sx={{ 
                        fontSize: isUltraCompact
                            ? (isSmallMobile ? '16px' : '20px')
                            : (isCompactMode
                                ? (isSmallMobile ? '20px' : '24px')
                                : (isMobile ? (isSmallMobile ? '24px' : '28px') : '38px')),
                        lineHeight: '1',
                        fontWeight: 'bold',
                        textAlign: 'center'
                    }}
                >
                                {day.congestion_level}
        </Typography>
    </Box>

    {/* イベント情報エリア */}
    {hasEvents && (
        <Box sx={{
            width: '100%',
            minHeight: isUltraCompact ? '10px' : (isSmallMobile ? '12px' : '14px'),
            backgroundColor: 'rgba(255, 255, 255, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1px 2px',
            overflow: 'visible'
        }}>
            <Typography 
                sx={{ 
                    fontSize: isUltraCompact ? '7px' : (isSmallMobile ? '8px' : '9px'),
                    color: '#333',
                    fontWeight: '600',
                    lineHeight: '1.1',
                    textAlign: 'center',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    hyphens: 'auto',
                    maxWidth: '100%'
                }}
                title={events.map(e => e.title).join(', ')}
            >
                {isToday
                    ? (isEnglish ? `${events.length} events` : `${events.length}件`)
                    : (events.length > 1 ? (isEnglish ? `${events.length} events` : `${events.length}件`) : events[0]?.title?.substring(0, 4))}
            </Typography>
        </Box>
    )}

    {/* 今日バッジ（数値可読性を邪魔しない右上表示） */}
    {isToday && isCurrentYear && (
        <Box sx={{
            position: 'absolute',
            bottom: '1px',
            left: '1px',
            backgroundColor: 'rgba(25, 118, 210, 0.92)',
            color: '#fff',
            borderRadius: '3px',
            padding: '0 3px',
            fontSize: isUltraCompact ? '7px' : (isSmallMobile ? '8px' : '9px'),
            fontWeight: 'bold',
            letterSpacing: '0.01em',
            zIndex: 3,
            lineHeight: 1.15,
            pointerEvents: 'none',
        }}>
            {isEnglish ? 'Today' : '今日'}
        </Box>
    )}

    {/* 予測バッジ（右下） */}
    {isPrediction && isCurrentYear && (
        <Box sx={{
            position: 'absolute',
            bottom: '1px',
            right: '1px',
            backgroundColor: 'rgba(85, 60, 154, 0.92)',
            color: '#fff',
            borderRadius: '3px',
            padding: '0 2px',
            fontSize: isUltraCompact ? '7px' : (isSmallMobile ? '8px' : '9px'),
            fontWeight: 'bold',
            letterSpacing: '0.02em',
            zIndex: 3,
            lineHeight: 1.1,
            pointerEvents: 'none',
        }}>
            {isEnglish ? 'Pred' : '予測'}
        </Box>
    )}

        </Box>
    );
};

WeeklyCell.propTypes = {
    day: PropTypes.shape({
        date: PropTypes.string.isRequired,
        congestion_level: PropTypes.number.isRequired,
        days_from_reference: PropTypes.number,
        is_today: PropTypes.bool,
        is_future: PropTypes.bool,
        is_empty: PropTypes.bool,
        source: PropTypes.string,
        weather_info: PropTypes.shape({
            weather: PropTypes.string,
            avg_temperature: PropTypes.number,
            temperature: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            humidity: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        })
    }).isRequired,
    isCurrentYear: PropTypes.bool,
    getTodaysDate: PropTypes.func.isRequired,
    isCompactMode: PropTypes.bool,
    isUltraCompact: PropTypes.bool,
    isEnglish: PropTypes.bool,
    events: PropTypes.arrayOf(PropTypes.shape({
        date: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired
    }))
};

export default WeeklyCell;
